import React, { useState } from "react";
import { string } from "prop-types";
import { EditOutlined, LoadingOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useSelector } from "react-redux";
import checkAuth from "../../../utils/checkAuth";
import { useCampaign } from "../../../api/campaigns";
import EditCampaignModal from "./EditCampaignModal";

// Small edit affordance for campaign list rows. Opens the same modal used on the
// campaign detail page, lazily fetching the product prices the modal needs.
export default function EditCampaignIcon({ campaignId }) {
  const [visible, setVisible] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const roles = useSelector(state => state.auth.roles);

  const { data: campaign, isFetching } = useCampaign({
    campaignId,
    includes: ["products", "productPrices"],
    enabled: visible,
  });

  if (!checkAuth.has(roles, ["admin"])) return null;

  const isLoading = visible && isFetching && !campaign;
  const Icon = isLoading ? LoadingOutlined : EditOutlined;

  return (
    <>
      {/* pointer-events:none so the tooltip overlay never intercepts a click
          meant for the icon beneath it (which would fall through to the row).
          offset y compensates for the icon's padded hit area so the tooltip
          sits on the glyph, not the top edge of the padding box. */}
      <Tooltip
        title="Edit campaign"
        // Controlled so the hover tooltip can't get stuck open: swapping the
        // icon component type and popping the modal over a stationary cursor
        // both swallow the mouseleave that would normally dismiss it.
        visible={tooltipOpen && !visible}
        onVisibleChange={setTooltipOpen}
        overlayStyle={{ pointerEvents: "none" }}
        align={{ offset: [0, 14] }}
      >
        <Icon
          // Expand the hit area (padding) beyond the glyph so clicks don't slip
          // through to the row; negative margin keeps the visual footprint tight.
          style={{
            color: "#1890ff",
            cursor: "pointer",
            padding: 14,
            margin: -14,
          }}
          onClick={e => {
            e.stopPropagation();
            setTooltipOpen(false);
            setVisible(true);
          }}
        />
      </Tooltip>
      {visible && campaign && (
        // Modal renders inside the table row's React subtree, so its clicks
        // bubble to the row's onClick (navigation) even though the DOM is
        // portaled. Isolate the whole modal subtree from the row handler.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <span onClick={e => e.stopPropagation()}>
          <EditCampaignModal
            campaign={campaign}
            visible={visible}
            onClose={() => setVisible(false)}
          />
        </span>
      )}
    </>
  );
}

EditCampaignIcon.propTypes = {
  campaignId: string,
};
